// Modal con detalle de un departamento clickeado en el mapa.
// Muestra stats agregadas, top juzgados internos y top 50 expedientes
// (click en uno abre el detalle del expediente en nueva pestaña).

import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Box, Typography, Grid, Paper, Skeleton, Alert,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import { dashEjecMapaCalorDetalleJWT } from '../../api/endpoints';

const PRIMARY = '#1769aa';
const BR = 4;

const fmtNum = (n) => Number(n || 0).toLocaleString('es-AR');
const fmtMoney = (n) => Number(n || 0).toLocaleString('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 });
const fmtFecha = (s) => s ? new Date(s).toLocaleDateString('es-AR') : '—';

export default function MapaCalorDetalleDialog({ open, departamento, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open || !departamento) return;
        let cancel = false;
        setLoading(true);
        setError('');
        setData(null);
        dashEjecMapaCalorDetalleJWT(departamento)
            .then(r => {
                if (cancel) return;
                if (r?.ok) setData(r.data);
                else setError(r?.mensaje || 'Error al cargar detalle');
            })
            .finally(() => { if (!cancel) setLoading(false); });
        return () => { cancel = true; };
    }, [open, departamento]);

    const stats = data?.stats || {};

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '4px' } }}>
            <DialogTitle sx={{ color: PRIMARY, fontWeight: 700 }}>
                Departamento: {departamento}
                <Typography variant="caption" sx={{ display: 'block', color: '#777', fontWeight: 400 }}>
                    Expedientes judiciales en trámite radicados acá
                </Typography>
            </DialogTitle>
            <DialogContent dividers>
                {loading ? (
                    <Box>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            {[1, 2].map(i => (<Grid key={i} size={{ xs: 12, sm: 6 }}>
                                <Skeleton variant="rounded" height={80} sx={{ borderRadius: `${BR}px` }} />
                            </Grid>))}
                        </Grid>
                        <Skeleton variant="rounded" height={250} sx={{ borderRadius: `${BR}px` }} />
                    </Box>
                ) : error ? (
                    <Alert severity="error">{error}</Alert>
                ) : !data ? null : (
                    <>
                        {/* Stats */}
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Paper sx={{ p: 2, borderRadius: `${BR}px`, bgcolor: '#f5f5f5' }}>
                                    <Typography variant="caption" sx={{ color: '#666', textTransform: 'uppercase' }}>Expedientes</Typography>
                                    <Typography variant="h4" sx={{ fontWeight: 700, color: PRIMARY }}>
                                        {fmtNum(stats.cantidad)}
                                    </Typography>
                                </Paper>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Paper sx={{ p: 2, borderRadius: `${BR}px`, bgcolor: '#fff3e0' }}>
                                    <Typography variant="caption" sx={{ color: '#666', textTransform: 'uppercase' }}>Monto demandado</Typography>
                                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#c62828' }}>
                                        {fmtMoney(stats.monto)}
                                    </Typography>
                                </Paper>
                            </Grid>
                        </Grid>

                        {/* Top juzgados */}
                        <Paper sx={{ p: 2, borderRadius: `${BR}px`, mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: PRIMARY }}>
                                Top 10 juzgados de {departamento}
                            </Typography>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 700 }}>Juzgado</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>Expedientes</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(data.topJuzgados || []).map((j, i) => (
                                            <TableRow key={i} hover>
                                                <TableCell sx={{ fontSize: '0.82rem' }}>{j.juzgado}</TableCell>
                                                <TableCell align="right" sx={{ fontSize: '0.82rem', fontWeight: 700, color: PRIMARY }}>
                                                    {fmtNum(j.cant)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {(data.topJuzgados || []).length === 0 && (
                                            <TableRow><TableCell colSpan={2} sx={{ color: '#888', fontStyle: 'italic', textAlign: 'center' }}>
                                                Sin juzgados con expedientes
                                            </TableCell></TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>

                        {/* Lista de expedientes */}
                        <Paper sx={{ p: 2, borderRadius: `${BR}px` }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: PRIMARY }}>
                                Top 50 expedientes más recientes
                            </Typography>
                            <TableContainer sx={{ maxHeight: 400 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 700 }}>ID</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>Carátula</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>Módulo</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>Inicio</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>Monto</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(data.expedientes || []).map(e => (
                                            <TableRow key={e.Id} hover sx={{ cursor: 'pointer' }}
                                                onClick={() => window.open(`/expedientes/${e.Id}?tipo=judicial`, '_blank')}>
                                                <TableCell sx={{ fontSize: '0.78rem' }}>{e.Id}</TableCell>
                                                <TableCell sx={{ fontSize: '0.8rem' }}>{e.Caratula}</TableCell>
                                                <TableCell sx={{ fontSize: '0.78rem', color: '#555' }}>{e.Modulo || '—'}</TableCell>
                                                <TableCell sx={{ fontSize: '0.78rem' }}>{fmtFecha(e.Fecha_Inicio)}</TableCell>
                                                <TableCell align="right" sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#c62828' }}>
                                                    {fmtMoney(e.monto)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {(data.expedientes || []).length === 0 && (
                                            <TableRow><TableCell colSpan={5} sx={{ color: '#888', fontStyle: 'italic', textAlign: 'center' }}>
                                                Sin expedientes
                                            </TableCell></TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cerrar</Button>
            </DialogActions>
        </Dialog>
    );
}
